import { useState, useEffect, useRef } from 'react';
import API from '../utils/api';

export default function FinishedProductPicker({ value, flavorValue, onChange, placeholder = 'اختر المنتج', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState('brand'); // brand | type | product
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [brands, setBrands] = useState([]);
  const searchRef = useRef();

  useEffect(() => {
    API.get('/finished-products').then(r => {
      setProducts(r.data);
      const b = [...new Set(r.data.map(p => p.brand).filter(Boolean))].sort();
      setBrands(b);
    });
  }, []);

  useEffect(() => {
    if (value && products.length) setSelectedProduct(products.find(p => p.id === +value) || null);
    else if (!value) setSelectedProduct(null);
  }, [value, products]);

  // ===== استخراج البراندات والأنواع =====
  // brands loaded from /api/brands via state

  const typesForBrand = [...new Set(
    products.filter(p => p.brand === selectedBrand).map(p => p.product_type).filter(Boolean)
  )].sort();

  const productsForType = products.filter(p =>
    p.brand === selectedBrand &&
    p.product_type === selectedType &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  // استخراج الرائحة من اسم المنتج (بعد إزالة نوع المنتج)
  const getFlavorFromName = (product) => {
    if (!product || !product.product_type) return product?.name || '';
    return product.name.replace(product.product_type, '').trim();
  };

  const openModal = () => {
    setStep('brand');
    setSelectedBrand('');
    setSelectedType('');
    setSearch('');
    setOpen(true);
  };

  const pickBrand = (brand) => {
    setSelectedBrand(brand);
    setSelectedType('');
    setSearch('');
    setStep('type');
  };

  const pickType = (type) => {
    setSelectedType(type);
    setSearch('');
    setStep('product');
    setTimeout(() => searchRef.current?.focus(), 100);
  };

  const pickProduct = (p) => {
    setSelectedProduct(p);
    onChange({ product: p, flavor: null });
    setOpen(false);
    setSearch('');
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelectedProduct(null);
    onChange({ product: null, flavor: null });
  };

  const goBack = () => {
    if (step === 'product') { setStep('type'); setSearch(''); }
    else if (step === 'type') { setStep('brand'); setSearch(''); }
  };

  // عدد المنتجات لكل برند
  const countForBrand = (brand) => products.filter(p => p.brand === brand).length;
  // عدد المنتجات لكل نوع ضمن البرند
  const countForType = (type) => products.filter(p => p.brand === selectedBrand && p.product_type === type).length;

  // ألوان البراندات
  const brandColors = {
    'JASMINE': '#6c3fc5',
    'LUVEAL': '#e91e8c',
    'CAVAYELO': '#0891b2',
    'MAROTA': '#d97706',
    'PURE PASSION': '#dc2626',
    'JASMINE NICHE': '#7c3aed',
    'VILARA': '#059669',
  };

  const brandColor = (b) => brandColors[b] || 'var(--primary)';

  return (
    <>
      {/* زر الاختيار */}
      <button type="button" disabled={disabled} onClick={openModal}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 8,
          border: `1.5px solid ${selectedProduct ? 'var(--primary)' : 'var(--border)'}`,
          background: selectedProduct ? 'var(--primary-light)' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, fontFamily: 'inherit', fontSize: '0.875rem', textAlign: 'right'
        }}>
        {selectedProduct ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
            {selectedProduct.brand && (
              <span style={{
                background: brandColor(selectedProduct.brand), color: 'white',
                borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0
              }}>{selectedProduct.brand}</span>
            )}
            <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{selectedProduct.product_type}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
              {getFlavorFromName(selectedProduct)}
            </span>
          </div>
        ) : (
          <span style={{ color: '#9ca3af' }}>📦 {placeholder}</span>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {selectedProduct && <span onClick={clear} style={{ color: 'var(--danger)', fontWeight: 700, padding: '0 4px' }}>✕</span>}
          <span style={{ color: 'var(--primary)', fontSize: '0.8rem' }}>▼</span>
        </div>
      </button>

      {/* المودال */}
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>

            {/* ===== خطوة 1: اختيار البرند ===== */}
            {step === 'brand' && (<>
              <div className="modal-header">
                <span className="modal-title">🏷️ اختر البرند</span>
                <button className="modal-close" onClick={() => setOpen(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {brands.map(b => (
                    <div key={b} onClick={() => pickBrand(b)}
                      style={{
                        padding: '14px 18px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${brandColor(b)}20`,
                        background: `${brandColor(b)}08`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${brandColor(b)}15`; e.currentTarget.style.borderColor = brandColor(b); }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${brandColor(b)}08`; e.currentTarget.style.borderColor = `${brandColor(b)}20`; }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          background: brandColor(b), color: 'white',
                          borderRadius: 8, padding: '4px 12px', fontWeight: 800, fontSize: '0.9rem'
                        }}>{b}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{countForBrand(b)} منتج</span>
                        <span style={{ color: brandColor(b) }}>←</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>)}

            {/* ===== خطوة 2: اختيار نوع المنتج ===== */}
            {step === 'type' && (<>
              <div className="modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={goBack}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--primary)' }}>→</button>
                  <span className="modal-title">
                    <span style={{ background: brandColor(selectedBrand), color: 'white', borderRadius: 6, padding: '2px 8px', fontSize: '0.8rem', marginLeft: 8 }}>{selectedBrand}</span>
                    اختر نوع المنتج
                  </span>
                </div>
                <button className="modal-close" onClick={() => setOpen(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {typesForBrand.map(t => (
                    <div key={t} onClick={() => pickType(t)}
                      style={{
                        padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                        border: '1.5px solid var(--border)', background: 'white',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}>
                      <span style={{ fontWeight: 700 }}>📦 {t}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{countForType(t)} رائحة</span>
                        <span style={{ color: 'var(--primary)' }}>←</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>)}

            {/* ===== خطوة 3: اختيار الرائحة (المنتج) ===== */}
            {step === 'product' && (<>
              <div className="modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={goBack}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--primary)' }}>→</button>
                  <span className="modal-title" style={{ fontSize: '0.9rem' }}>
                    <span style={{ background: brandColor(selectedBrand), color: 'white', borderRadius: 6, padding: '2px 8px', fontSize: '0.75rem', marginLeft: 6 }}>{selectedBrand}</span>
                    {selectedType}
                  </span>
                </div>
                <button className="modal-close" onClick={() => setOpen(false)}>✕</button>
              </div>
              <div className="modal-body" style={{ padding: '12px 16px' }}>
                <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="🔍 ابحث برائحة..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: '0.9rem', marginBottom: 10, boxSizing: 'border-box' }} />

                <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {productsForType.length === 0
                    ? <div className="text-muted" style={{ textAlign: 'center', padding: 20 }}>لا توجد نتائج</div>
                    : productsForType.map(p => {
                        const flavor = getFlavorFromName(p);
                        return (
                          <div key={p.id} onClick={() => pickProduct(p)}
                            style={{
                              padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                              border: '1.5px solid var(--border)', background: 'white',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              transition: 'all 0.15s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '0.85rem' }}>🌸</span>
                              <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{flavor || p.name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {p.price > 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>$ {p.price}</span>}
                              <span style={{ color: 'var(--primary)' }}>←</span>
                            </div>
                          </div>
                        );
                      })
                  }
                </div>
                <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 8, textAlign: 'center' }}>
                  {productsForType.length} رائحة
                </div>
              </div>
            </>)}

          </div>
        </div>
      )}
    </>
  );
}
